/**
 * Confirm Delete Dialog.
 *
 * A modal that previews the full impact of deleting a record before
 * the user confirms. Shows what child records will be cascade-deleted,
 * what references will be cleared, and what blocks exist. For high-
 * impact deletes, requires typing the record identifier to confirm.
 */

import { useState, useEffect } from "react";
import { AlertTriangle, Trash2, Link2Off, ShieldAlert } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import ActionDrawer from "@/components/ui/ActionDrawer";
import Button from "@/components/ui/Button";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface DeletePreview {
  entity_type: string;
  entity_id: string;
  entity_label: string;
  can_delete: boolean;
  cascades: Array<{ type: string; count: number; label: string }>;
  unlinks: Array<{ type: string; count: number; label: string }>;
  blocks: Array<{
    type: string;
    count: number;
    label: string;
    resolution: string;
  }>;
}

interface ConfirmDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** API path prefix (e.g., "/api/v1/purchase-orders"). */
  apiPath: string;
  /** Entity ID to delete. */
  entityId: string;
  /** Query key to invalidate on success. */
  queryKey: string;
  /** Called after successful deletion. */
  onDeleted?: () => void;
}

function ConfirmDeleteDialog({
  isOpen,
  onClose,
  apiPath,
  entityId,
  queryKey,
  onDeleted,
}: ConfirmDeleteDialogProps) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState("");

  const { data: preview, isLoading } = useQuery<DeletePreview>({
    queryKey: ["delete-preview", entityId],
    queryFn: async () =>
      (await apiClient.get(`${apiPath}/${entityId}/delete-preview`)).data,
    enabled: isOpen && !!entityId,
  });

  // Reset confirm text when dialog opens.
  useEffect(() => {
    if (isOpen) setConfirmText("");
  }, [isOpen]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`${apiPath}/${entityId}?force=true`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      toast.success("Deleted", preview?.entity_label || "Record deleted");
      onClose();
      onDeleted?.();
    },
    onError: () => toast.error("Delete failed"),
  });

  const totalImpact =
    (preview?.cascades.reduce((s, c) => s + c.count, 0) || 0) +
    (preview?.unlinks.reduce((s, u) => s + u.count, 0) || 0);
  const needsConfirmation =
    totalImpact > 0 || (preview?.blocks.length || 0) > 0;
  const confirmLabel = preview?.entity_label || "";
  const isConfirmed =
    !needsConfirmation ||
    confirmText.trim().toLowerCase() === confirmLabel.trim().toLowerCase();

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Record"
      description={
        preview
          ? `Review the impact of deleting ${preview.entity_label}`
          : "Loading impact preview..."
      }
      icon={Trash2}
      size="md"
    >
      {isLoading || !preview ? (
        <LoadingSpinner className="py-8" />
      ) : (
        <div className="space-y-4">
          {/* No impact — simple delete */}
          {!needsConfirmation && (
            <div className="rounded-[var(--radius-md)] bg-[var(--fill-success)] p-4">
              <p className="text-sm text-[var(--text-success)]">
                No related records found. This record can be safely deleted.
              </p>
            </div>
          )}

          {/* Cascades — will be deleted */}
          {preview.cascades.length > 0 && (
            <div className="rounded-[var(--radius-md)] border border-[var(--text-danger)]/20 bg-[var(--fill-danger)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-[var(--text-danger)]" />
                <p className="text-sm font-semibold text-[var(--text-danger)]">
                  Will be permanently deleted
                </p>
              </div>
              <ul className="space-y-1">
                {preview.cascades.map((c) => (
                  <li
                    key={c.type}
                    className="text-sm text-[var(--text-danger)] flex items-center gap-2"
                  >
                    <span className="h-1 w-1 rounded-full bg-[var(--text-danger)] shrink-0" />
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unlinks — references cleared */}
          {preview.unlinks.length > 0 && (
            <div className="rounded-[var(--radius-md)] border border-[var(--text-warning)]/20 bg-[var(--fill-warning)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Link2Off className="h-4 w-4 text-[var(--text-warning)]" />
                <p className="text-sm font-semibold text-[var(--text-warning)]">
                  References will be cleared
                </p>
              </div>
              <ul className="space-y-1">
                {preview.unlinks.map((u) => (
                  <li
                    key={u.type}
                    className="text-sm text-[var(--text-warning)] flex items-center gap-2"
                  >
                    <span className="h-1 w-1 rounded-full bg-[var(--text-warning)] shrink-0" />
                    {u.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Blocks — need resolution or force */}
          {preview.blocks.length > 0 && (
            <div className="rounded-[var(--radius-md)] border border-[var(--text-danger)]/20 bg-[var(--fill-danger)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="h-4 w-4 text-[var(--text-danger)]" />
                <p className="text-sm font-semibold text-[var(--text-danger)]">
                  Blocking references (will be cleared on force delete)
                </p>
              </div>
              <ul className="space-y-2">
                {preview.blocks.map((b) => (
                  <li key={b.type} className="text-sm">
                    <p className="text-[var(--text-danger)]">{b.label}</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                      {b.resolution}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Confirmation input for high-impact deletes */}
          {needsConfirmation && (
            <div>
              <p className="text-xs text-[var(--text-secondary)] mb-1.5">
                Type{" "}
                <span className="font-mono font-semibold text-[var(--text-primary)]">
                  {confirmLabel}
                </span>{" "}
                to confirm deletion:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={confirmLabel}
                className="w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] font-mono placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteMutation.mutate()}
              isLoading={deleteMutation.isPending}
              disabled={!isConfirmed}
              className="bg-[var(--color-interactive-danger)] text-white hover:opacity-90 disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {preview.blocks.length > 0 ? "Force Delete" : "Delete"}
            </Button>
          </div>
        </div>
      )}
    </ActionDrawer>
  );
}

export default ConfirmDeleteDialog;
